import { ref, Ref } from 'vue';
import CouponsService from '@/api/services/coupons.service';
import { ICoupon } from '@/interfaces/ICoupon';
import { IOrder } from '@/interfaces/IOrder';
import { IDataOptions } from '@/interfaces/IDataOptions';
import { AlertType } from '@/interfaces/IAlert';
import { PaymentMethod } from '@/enums/PaymentMethod';
import { OrderType } from '@/enums/OrderEnums';
import { DiscountType } from '@/enums/DiscountType';
import { useUi } from './use-ui';
import { useOrders } from './use-orders';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { useDiscounts } from './use-discounts';

export function useCoupons() {
  const ui = useUi();
  const ordersHelper = useOrders();
  const restaurantStore = useRestaurantStore();
  const discountsHelper = useDiscounts();
  const loading = ref(false);

  async function listCoupons(
    options?: IDataOptions & { status?: 'valid' | 'expired' | 'all' }
  ) {
    try {
      loading.value = true;
      return await CouponsService.list(
        restaurantStore.restaurant.id,
        restaurantStore.selectedBranch.id,
        options
      );
    } catch (error) {
      ui.serverError(error, 'Error al cargar cupones');
      throw error;
    } finally {
      loading.value = false;
    }
  }

  async function getCoupon(couponId: number): Promise<ICoupon | null> {
    try {
      loading.value = true;
      return await CouponsService.getOne(
        restaurantStore.restaurant.id,
        restaurantStore.selectedBranch.id,
        couponId
      );
    } catch (error) {
      ui.serverError(error, 'Error al cargar cupón');
      return null;
    } finally {
      loading.value = false;
    }
  }

  async function createCoupon(data: any): Promise<ICoupon | null> {
    try {
      loading.value = true;

      // Convert payment methods array to bitmask
      if (data.allowedPaymentMethods?.length) {
        data.allowedPaymentMethods = CouponsService.paymentMethodsToBitmask(
          data.allowedPaymentMethods
        );
      } else {
        data.allowedPaymentMethods = null;
      }

      // Convert days array to bitmask
      if (data.allowedDays?.length) {
        data.allowedDays = data.allowedDays.reduce(
          (mask: number, day: number) => mask | (1 << day),
          0
        );
      } else {
        data.allowedDays = null;
      }

      const coupon = await CouponsService.create(
        restaurantStore.restaurant.id,
        restaurantStore.selectedBranch.id,
        data
      );

      ui.showToast(AlertType.Success, 'Cupón creado exitosamente');
      return coupon;
    } catch (error) {
      ui.serverError(error, 'Error al crear cupón');
      return null;
    } finally {
      loading.value = false;
    }
  }

  async function updateCoupon(
    couponId: number,
    data: any
  ): Promise<ICoupon | null> {
    try {
      loading.value = true;

      // Convert payment methods array to bitmask
      if (data.allowedPaymentMethods !== undefined) {
        if (data.allowedPaymentMethods?.length) {
          data.allowedPaymentMethods = CouponsService.paymentMethodsToBitmask(
            data.allowedPaymentMethods
          );
        } else {
          data.allowedPaymentMethods = null;
        }
      }

      // Convert days array to bitmask
      if (data.allowedDays !== undefined) {
        if (data.allowedDays?.length) {
          data.allowedDays = data.allowedDays.reduce(
            (mask: number, day: number) => mask | (1 << day),
            0
          );
        } else {
          data.allowedDays = null;
        }
      }

      const coupon = await CouponsService.update(
        restaurantStore.restaurant.id,
        restaurantStore.selectedBranch.id,
        couponId,
        data
      );

      ui.showToast(AlertType.Success, 'Cupón actualizado');
      return coupon;
    } catch (error) {
      ui.serverError(error, 'Error al actualizar cupón');
      return null;
    } finally {
      loading.value = false;
    }
  }

  async function deleteCoupon(couponId: number): Promise<boolean> {
    try {
      loading.value = true;

      await CouponsService.delete(
        restaurantStore.restaurant.id,
        restaurantStore.selectedBranch.id,
        couponId
      );

      ui.showToast(AlertType.Success, 'Cupón eliminado');
      return true;
    } catch (error) {
      ui.serverError(error, 'Error al eliminar cupón');
      return false;
    } finally {
      loading.value = false;
    }
  }

  async function getCouponUsageHistory(
    couponId: number,
    options?: IDataOptions
  ) {
    try {
      loading.value = true;
      return await CouponsService.getUsageHistory(
        restaurantStore.restaurant.id,
        restaurantStore.selectedBranch.id,
        couponId,
        options
      );
    } catch (error) {
      ui.serverError(error, 'Error al cargar historial de uso');
      return {
        items: [],
        total: 0,
        page: 1,
        take: 10,
      };
    } finally {
      loading.value = false;
    }
  }

  async function validateCouponForOrder(order: IOrder, code: string) {
    try {
      loading.value = true;
      return await CouponsService.validateForOrder(
        restaurantStore.restaurant.id,
        restaurantStore.selectedBranch.id,
        order.id,
        code
      );
    } catch (error) {
      // Don't show server error here, let the caller handle it
      return {
        valid: false,
        error: 'Error al validar cupón',
      };
    } finally {
      loading.value = false;
    }
  }

  async function applyCouponToOrder(
    order: Ref<IOrder>,
    code: string
  ): Promise<boolean> {
    try {
      loading.value = true;

      const result = await CouponsService.applyToOrder(
        restaurantStore.restaurant.id,
        restaurantStore.selectedBranch.id,
        order.value.id,
        code
      );

      // Refresh the order to get updated totals
      await ordersHelper.refreshOrder({ order: order.value });

      ui.showToast(AlertType.Success, 'Cupón aplicado exitosamente');
      return true;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Error al aplicar cupón';
      ui.showToast(AlertType.Error, message);
      return false;
    } finally {
      loading.value = false;
    }
  }

  function formatCouponValue(coupon: ICoupon): string {
    const currencySymbol = restaurantStore.restaurant.currencySymbol || '$';

    if (coupon.isPercentage) {
      const suffix = coupon.maxDiscountAmount
        ? ` (máx ${currencySymbol}${coupon.maxDiscountAmount})`
        : '';
      return `${coupon.value}%${suffix}`;
    } else {
      return `${currencySymbol}${coupon.value.toFixed(2)}`;
    }
  }

  function formatCouponStatus(
    coupon: ICoupon
  ): {
    text: string;
    color: string;
  } {
    if (!coupon.isActive) {
      return { text: 'Inactivo', color: 'grey' };
    }

    const now = new Date();
    const validFrom = coupon.validFrom ? new Date(coupon.validFrom) : null;
    const validUntil = coupon.validUntil ? new Date(coupon.validUntil) : null;

    if (validFrom && now < validFrom) {
      return { text: 'Pendiente', color: 'orange' };
    }

    if (validUntil && now > validUntil) {
      return { text: 'Expirado', color: 'red' };
    }

    if (coupon.maxUses && coupon.currentUses >= coupon.maxUses) {
      return { text: 'Agotado', color: 'red' };
    }

    return { text: 'Activo', color: 'success' };
  }

  function getPaymentMethodsArray(bitmask: number | null): PaymentMethod[] {
    if (!bitmask) return [];
    return CouponsService.bitmaskToPaymentMethods(bitmask);
  }

  function getDaysArray(bitmask: number | null): number[] {
    if (!bitmask) return [];
    const days: number[] = [];
    for (let i = 0; i < 7; i++) {
      if (bitmask & (1 << i)) {
        days.push(i);
      }
    }
    return days;
  }

  function formatCouponRestrictions(coupon: ICoupon): string[] {
    const restrictions: string[] = [];

    if (coupon.minOrderAmount) {
      const currency = restaurantStore.restaurant.currencySymbol || '$';
      restrictions.push(`Pedido mínimo: ${currency}${coupon.minOrderAmount}`);
    }

    if (coupon.maxOrderAmount) {
      const currency = restaurantStore.restaurant.currencySymbol || '$';
      restrictions.push(`Pedido máximo: ${currency}${coupon.maxOrderAmount}`);
    }

    if (coupon.maxUsesPerCustomer) {
      restrictions.push(`${coupon.maxUsesPerCustomer} uso(s) por cliente`);
    }

    if (coupon.allowedPaymentMethods) {
      const methods = getPaymentMethodsArray(coupon.allowedPaymentMethods);
      if (methods.length > 0 && methods.length < 4) {
        restrictions.push('Métodos de pago limitados');
      }
    }

    if (coupon.allowedOrderTypes?.length) {
      restrictions.push('Tipos de pedido limitados');
    }

    if (coupon.allowedDays) {
      restrictions.push('Días específicos');
    }

    if (coupon.allowedHours?.length) {
      restrictions.push('Horarios específicos');
    }

    return restrictions;
  }

  return {
    loading,
    listCoupons,
    getCoupon,
    createCoupon,
    updateCoupon,
    deleteCoupon,
    getCouponUsageHistory,
    validateCouponForOrder,
    applyCouponToOrder,
    formatCouponValue,
    formatCouponStatus,
    formatCouponRestrictions,
    getPaymentMethodsArray,
    getDaysArray,
  };
}
